import type { GroupedPosition, Position } from '../types'

/**
 * Group positions by role and level
 * Creates a hierarchical structure for the accordion display
 */
export const groupPositionsByRoleAndLevel = (
  positions: Array<Position>,
): Array<GroupedPosition> => {
  // Group by role first
  const groupedByRole = positions.reduce(
    (acc, position) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!acc[position.role]) {
        acc[position.role] = []
      }
      acc[position.role].push(position)
      return acc
    },
    {} as Record<string, Array<Position>>,
  )

  // Then group by level within each role
  return Object.entries(groupedByRole)
    .map(([role, rolePositions]) => {
      const groupedByLevel = rolePositions.reduce(
        (acc, position) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!acc[position.level]) {
            acc[position.level] = []
          }
          acc[position.level].push(position)
          return acc
        },
        {} as Record<string, Array<Position>>,
      )

      const levels = Object.entries(groupedByLevel)
        .map(([level, levelPositions]) => ({
          level,
          positions: levelPositions,
        }))
        .sort((a, b) => {
          // Sort levels (IC1, IC2, IC3, etc.)
          const aNum = parseInt(a.level.replace(/\D/g, '')) || 0
          const bNum = parseInt(b.level.replace(/\D/g, '')) || 0
          return aNum - bNum
        })

      return {
        role,
        levels,
      }
    })
    .sort((a, b) => a.role.localeCompare(b.role)) // Sort roles alphabetically
}

export const getUniqueRoles = (positions: Array<Position>): Array<string> => {
  return [...new Set(positions.map((p) => p.role))].sort()
}

export const getUniqueLevels = (positions: Array<Position>): Array<string> => {
  return [...new Set(positions.map((p) => p.level))].sort((a, b) => {
    const aNum = parseInt(a.replace(/\D/g, '')) || 0
    const bNum = parseInt(b.replace(/\D/g, '')) || 0
    return aNum - bNum
  })
}

export const getPositionSummary = (positions: Array<Position>): string => {
  const roleCount = getUniqueRoles(positions).length
  const levelCount = getUniqueLevels(positions).length

  return `${positions.length} position${positions.length !== 1 ? 's' : ''} across ${roleCount} role${roleCount !== 1 ? 's' : ''} and ${levelCount} level${levelCount !== 1 ? 's' : ''}`
}
